import { instantOf } from '../freshness.js'
import { curveModel, lastChance, swingRows } from './flowModel.js'
import { CALLOUT_WINDOW_SECONDS, calloutAge, calloutLine, calloutWhen, isCallout } from './liveClock.js'
import { STALE_AFTER_MS, sourceAge } from './model.js'
import { shotChart } from './shotModel.js'

// What the cards and the game view say beyond the score: the win-probability strip, the latest callout, the five on
// the floor, and which sections a game has. P6-330b (GN-3b).
//
// Pure functions over the producer's cards and game blobs (`utils/hoops_projection.py`). The clock arithmetic is
// `liveClock.js`'s, the shots' is `shotModel.js`'s and the curve's and the swings' is `flowModel.js`'s.
//
// ═══════════════════════════════════════════════════════════════════════════
// LIVE ONLY, AND ONLY WHAT IS THERE
// ═══════════════════════════════════════════════════════════════════════════
//
// The strip, the callout and the five on the floor belong to a game that is being played. Two of them ride on FINAL
// cards too, frozen with the game (`win_probability` and `callout`, "Both ride on final cards too ... which cards
// SHOW them is the page's"), and a final game's box score still flags the last five a side, which would name ten
// players "on the floor" of a finished game. So every function here that says something about the game NOW takes the
// status and answers `null` for anything but `live`. A stale card is a live game the page has lost touch with, and
// what it last knew (its strip, its callout, its five) is still what it last knew, so `stale` is not a different
// answer: it is the same status, drawn as of then.
//
// And what is absent is not drawn. The strip has no value in preseason (ESPN publishes none), a card whose box
// score is unread has no `on_floor`, a callout older than four game minutes is gone, and a section whose data does
// not exist has no segment: no heading with nothing under it, no empty chart, no control that opens nothing.

/**
 * The strip's two figures from the HOME team's chance (0 to 1): the away team's percentage, rounded, and the home
 * team's as the rest, so the two always make a hundred (the design's `a = round((1 - p) * 100)`, home `100 - a`).
 * `null` for a value that is not a chance.
 */
export function stripFrom(home) {
  if (typeof home !== 'number' || !Number.isFinite(home) || home < 0 || home > 1) return null
  const away = Math.round((1 - home) * 100)
  return { away, home: 100 - away }
}

/** A live card's strip, or `null`: not live, or no `win_probability` (every preseason game). */
export function cardStrip(card) {
  return card?.status === 'live' ? stripFrom(card.win_probability?.home) : null
}

/**
 * The strip the game view's hero draws, or `null`.
 *
 * The game's own curve says where the game is if it has been read (its last point IS the card's `win_probability`
 * as of the same build), so the hero's strip and the Flow curve's end can never disagree by a poll; the card's value
 * is what is used while the game's object has none. `status` is the game's own, the more advanced of the card's and
 * the object's, so a game that has just ended draws no strip.
 */
export function heroStrip({ game, card, status }) {
  if (status !== 'live') return null
  return stripFrom(lastChance(game?.win_probability) ?? card?.win_probability?.home)
}

// ═══════════════════════════════════════════════════════════════════════════
// THE STRIP SAYS ESPN'S AGE ONCE IT IS OLD (P6-334, GN-7; GN-3b's concern 2)
// ═══════════════════════════════════════════════════════════════════════════
//
// The strip is ESPN's, and ESPN is asked on its own (every 15 s tick while a game is live, so a healthy stamp is at most
// about 30 s old). The card around it says the NBA's age (`NBA · 14s`), and the two clocks are independent: the card's
// `as_of` is the NBA files' oldest fetch and the strip's `win_probability.as_of` is ESPN's own. So a stalled ESPN would show
// an old chance on a card whose NBA age reads fresh, and the page would be saying nothing about it. HP5 says an age is never
// hidden; the design's strip has none, and the brief asks for one only when it matters:
//
//   WHEN   ESPN's stamp is MORE than 120 s older than the page's clock: the board's own stale threshold, with the brief's
//          edge (absent at exactly 120 s, present at 121). The board's rule for a CARD going stale is `>=`; this is not that
//          rule, it is the brief's, and the two differ only at the instant of 120.000 s.
//   WHAT   `sourceAge`'s own wording, as every other age on the page: `ESPN · 2m`. Nothing changes while the stamp is
//          fresh, and a stamp the page cannot read is said in words (`ESPN · age unknown`), never hidden, because a strip
//          that carries no age on a chance it cannot date is claiming a freshness it does not have.
//   WHOSE  ESPN's, and never the NBA's. On a card that is `card.win_probability.as_of`. In the hero it is the game's
//          `win_probability_as_of` when the strip came from the game's own curve, and the card's when it did not: the
//          stamp follows the number, so the age under the strip is the age of the chance above it.

/** More than this much older than the page's clock and the strip says ESPN's age. The board's own threshold. */
export const ESPN_AGE_AFTER_MS = STALE_AFTER_MS

const sourceOf = (value) => (typeof value === 'string' && value !== '' ? value : null)

/** The stamp of the strip a card draws, ESPN's own: `{ source, stampMs }`, or `null` when the card draws no strip. */
export function cardStripStamp(card) {
  if (cardStrip(card) === null) return null
  const win = card.win_probability
  return { source: sourceOf(win?.source), stampMs: instantOf(win?.as_of) }
}

/**
 * The stamp of the strip the hero draws, ESPN's own, or `null` when the hero draws none. It follows the NUMBER: `heroStrip`
 * reads the game's own curve first and the card's value only when the game has no curve, and this reads the stamp of
 * whichever it read.
 */
export function heroStripStamp({ game, card, status }) {
  if (heroStrip({ game, card, status }) === null) return null
  if (lastChance(game?.win_probability) !== null) {
    return { source: sourceOf(game.win_probability_source), stampMs: instantOf(game.win_probability_as_of) }
  }
  const win = card?.win_probability
  return { source: sourceOf(win?.source), stampMs: instantOf(win?.as_of) }
}

/**
 * What the strip says about ESPN's age: `ESPN · 2m`, or `null` while the stamp is fresh (or there is no strip).
 *
 * The comparison is on the milliseconds, not on `ageText`'s rounded seconds: "more than 120 s" is a fact about the stamp, and
 * 120.4 s old is more than 120.
 */
export function espnAgeLine(stamp, nowMs) {
  if (stamp === null) return null
  if (stamp.stampMs !== null && nowMs - stamp.stampMs <= ESPN_AGE_AFTER_MS) return null
  return sourceAge(stamp.source, stamp.stampMs, nowMs)
}

/** A live card's strip age, or `null`. */
export const cardStripAge = (card, nowMs) => espnAgeLine(cardStripStamp(card), nowMs)

/** The hero's strip age, or `null`. */
export const heroStripAge = (args, nowMs) => espnAgeLine(heroStripStamp(args), nowMs)

/**
 * The callout a live card shows, or `null`.
 *
 * Only if the card is live, the callout is well formed, and it is within FOUR minutes of game clock of the card's
 * own (`liveClock.js`: twelve-minute quarters, five-minute overtimes, a negative age taken as zero). A callout whose
 * age cannot be worked out (no clock on the card, a clock that is not a clock) is NOT shown: the window is its
 * expiry, and a callout that cannot be shown to be recent is not recent.
 */
export function liveCallout(card) {
  if (card?.status !== 'live') return null
  const callout = card.callout
  if (!isCallout(callout)) return null
  const age = calloutAge(callout, card)
  if (age === null || age > CALLOUT_WINDOW_SECONDS) return null
  const side = callout.team === card.away?.tricode ? 'away' : callout.team === card.home?.tricode ? 'home' : null
  return { line: calloutLine(callout), when: calloutWhen(callout), kind: callout.kind, team: callout.team, side, age }
}

const isPlayer = (player) =>
  player !== null && typeof player === 'object' && (Number.isInteger(player.person_id) || typeof player.name === 'string')

/**
 * Both sides' players, as `{ away: [...], home: [...] }`, or `null` when there is nobody to draw.
 *
 * ⚠️ NEVER FIVE, ONLY WHAT IS THERE. The producer publishes the box score's flags as they are ("four, six or nobody
 * on the floor is published as it is, never padded to five from the bench and never trimmed to five"), and what the
 * feed flags between periods, at halftime and around a substitution has not been measured. A side is drawn with as
 * many as it has, one with none is drawn as a side with none, and a game with nobody on EITHER side draws no
 * section at all rather than a heading over two empty rows.
 */
function sidesOrNull(away, home) {
  const sides = { away: away.filter(isPlayer), home: home.filter(isPlayer) }
  return sides.away.length + sides.home.length === 0 ? null : sides
}

/** A live card's five on the floor, from its `on_floor`, or `null`: not live, none read, or nobody on either side. */
export function cardFloor(card) {
  if (card?.status !== 'live') return null
  const floor = card.on_floor
  if (floor === null || typeof floor !== 'object') return null
  return sidesOrNull(Array.isArray(floor.away) ? floor.away : [], Array.isArray(floor.home) ? floor.home : [])
}

/**
 * The game view's five on the floor, from the box score's `oncourt`, or `null`.
 *
 * Live only, whatever the blob says: a final game's blob still flags the last five a side (the recorded final has
 * five each), and the design draws the section only while the game is live. The design's "always" (HANDOFF flag 1) is
 * about WIDTH, not status: the game view shows them at every width, where a card shows them from 720px.
 */
export function gameFloor(game, status) {
  if (status !== 'live') return null
  const on = (players) => (Array.isArray(players) ? players.filter((player) => player?.oncourt === true) : [])
  return sidesOrNull(on(game?.players?.away), on(game?.players?.home))
}

/** The four sections of the game view, in the design's order, by key. */
export const SECTION_LABELS = Object.freeze({ box: 'Box score', plays: 'Plays', shots: 'Shot chart', flow: 'Flow' })

/**
 * The sections the game view offers, as `{ key, label }`.
 *
 * Below 1000px: Box score and Plays, always, then Shot chart if the game has a shot with a position, then Flow if
 * it has a curve to draw or a callout to list. From 1000px the box score stays open on the left and is not a
 * section, so the list starts at Plays. Nothing is in the list for data that does not exist: that is what makes "no
 * empty segment and no dead control" true by construction.
 */
export function sectionsFor({ hasShots, hasFlow, wide }) {
  const keys = [...(wide ? [] : ['box']), 'plays', ...(hasShots ? ['shots'] : []), ...(hasFlow ? ['flow'] : [])]
  return keys.map((key) => ({ key, label: SECTION_LABELS[key] }))
}

/** The section open: the one chosen if this width and this game still have it, else the design's default. */
export function activeSection(sections, chosen, wide) {
  return sections.some((section) => section.key === chosen) ? chosen : wide ? 'plays' : 'box'
}

/**
 * Everything the game view draws from the game's own object that is NOT the box score or the plays, worked out once
 * per object (the caller memoises on the object, because the page re-renders every second and the shots and the
 * curve are the biggest things on it).
 */
export function gameFun(game) {
  const chart = shotChart(game)
  const curve = curveModel(game?.win_probability)
  const swings = swingRows(game)
  return {
    chart,
    curve,
    swings,
    hasShots: chart.located > 0,
    hasFlow: curve !== null || swings.length > 0,
  }
}
